/**
 * ATL-888 — restart the app.
 *
 * Isolated from `orgReadCutoverWatch` so that the decision rule stays pure and testable, and so
 * the one place in the codebase that restarts the app on its own initiative is a single named
 * function rather than a platform branch inlined into a React effect.
 *
 * Restarting is what a read-cutover switch needs: the subscriptions and the initial atom load are
 * pinned to whatever startup decided, so only a fresh start re-runs `primeOrganizationForReadCutover`
 * and rebuilds them against the store the flag now names.
 *
 * ⚠️ `Updates.reloadAsync()` reloads using the MOST RECENTLY DOWNLOADED update, not necessarily the
 * bundle currently running. `app.json` sets no `checkAutomatically`, so the default (`ON_LOAD`)
 * fetches available updates in the background on every launch — which means a rollback restart may
 * also move the till onto a newer bundle. That is usually desirable and it is the documented
 * behaviour of the API, but it is a real coupling worth knowing during an incident: the device that
 * comes back may not be running the code that went down. There is no reload-in-place variant.
 */

import { Platform } from 'react-native'
import * as Updates from 'expo-updates'

export interface ReloadAppDeps {
  /** Injected for tests. Defaults to the real platform. */
  platformOS?: string
  reloadAsync?: () => Promise<void>
  reloadLocation?: () => void
  logger?: Pick<Console, 'error'>
  /** The log tag (ATL-1452's bundle lockout restarts through here too). */
  label?: string
}

/**
 * Restart the app on whichever platform this is.
 *
 * On web `location.reload()` is synchronous from the caller's point of view. On native
 * `Updates.reloadAsync()` is `async`: it fulfils immediately before the reload instruction is sent,
 * and it REJECTS (rather than throwing synchronously) when updates are disabled — as in Expo Go and
 * some dev clients. It is therefore not awaited, and both the rejection and a synchronous throw are
 * caught, because a failed reload means this device is still serving the store an operator has just
 * declared unsafe and that must not be silent.
 *
 * Never throws.
 */
export const reloadApp = ({
  platformOS = Platform.OS,
  reloadAsync = Updates.reloadAsync,
  reloadLocation,
  logger = console,
  label = 'read-cutover',
}: ReloadAppDeps = {}): void => {
  const failed = (error: unknown): void => {
    logger.error(
      `[${label}] app reload failed; this device did NOT restart and must be restarted by hand`,
      { platformOS, error }
    )
  }

  if (platformOS === 'web') {
    try {
      const reload =
        reloadLocation ??
        (typeof window !== 'undefined' ? () => window.location.reload() : undefined)
      if (!reload) {
        failed(new Error('no window.location available'))
        return
      }
      reload()
    } catch (error) {
      failed(error)
    }
    return
  }

  try {
    // Deliberately not awaited: on success the runtime is gone before the promise could settle.
    void Promise.resolve(reloadAsync()).catch(failed)
  } catch (error) {
    // `reloadAsync` throws synchronously when updates are disabled (Expo Go, some dev clients).
    failed(error)
  }
}
